import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { DIMENSIONS, DIM_COLORS, type Dimension } from '@/features/quiz';
import {
  computeNeutralScores,
  NEUTRAL_DIMENSIONS,
  type NeutralDimension,
} from '@/features/quiz/neutralScores';
import { useCostOfLiving } from '@/features/quiz/cost-of-living';
import { countriesQuery } from '@/data';
import { cn } from '@/components/ui/cn';
import { localizePath } from '@/i18n/paths';
import { uiLang } from '@/i18n/lang';

// Map a home-dimension slug to the corresponding topic landing-page URL slug.
// Keeps DimensionsGrid free of a runtime dependency on the wiki feature module.
const TOPIC_URL_SLUG: Record<Dimension | 'property_tax', string> = {
  visa: 'visa',
  tax: 'tax',
  property_tax: 'property-tax',
  cost: 'cost',
  climate: 'climate',
  healthcare: 'healthcare',
  safety: 'safety',
  language: 'language',
  jobs: 'jobs',
  family: 'family',
  banking: 'banking',
};

/**
 * The example range under each card, computed rather than written down.
 *
 * These used to be strings in the locale file: "e.g. Portugal 7.1 · UAE 3.4".
 * Checked against `computeNeutralScores`, which is the only thing in the
 * product that produces a 0-10 dimension score, not one of the eleven was
 * right. Cost claimed Portugal 7.1 and UAE 3.4 against real values of 3.3 and
 * 0.0. Climate claimed Italy 9.5 and Thailand 4.0 when Thailand is the higher
 * of the two, so the card inverted the comparison it was making. Four cards
 * named countries the product does not carry at all: Belgium, the USA, Russia,
 * and the Netherlands, which is in the file but has no metrics. Three named
 * dimensions the scorer does not compute.
 *
 * They were plausible, specific to one decimal, and printed under a named
 * source and year, which is what made them read as measurements. This is the
 * same defect as the fabricated cost fields, on the most visited page in the
 * product.
 *
 * So the numbers now come from the same function the quiz result uses, over
 * the same data file, and a dimension the scorer does not compute shows no
 * numbers at all rather than borrowed ones.
 */
function useDimensionRange(slug: Dimension | 'property_tax') {
  const { data: countriesData } = useQuery(countriesQuery());
  const { data: costFile } = useCostOfLiving();
  const { i18n } = useTranslation();
  const lang = i18n.language === 'ru' ? 'ru' : 'en';

  return useMemo(() => {
    if (!NEUTRAL_DIMENSIONS.includes(slug as NeutralDimension)) return null;
    const list = countriesData?.countries.filter((c) => c.metrics) ?? [];
    if (list.length === 0) return null;

    const scored = list
      .map((c) => {
        const scores = computeNeutralScores(c.metrics!, costFile, c.code);
        const value = scores[slug as NeutralDimension];
        return typeof value === 'number'
          ? { name: lang === 'ru' ? c.name_ru : c.name_en, value }
          : null;
      })
      .filter((x): x is { name: string; value: number } => x !== null)
      .sort((a, b) => b.value - a.value);

    if (scored.length < 2) return null;
    const high = scored[0];
    const low = scored[scored.length - 1];
    return {
      high: { name: high.name, score: (high.value * 10).toFixed(1) },
      low: { name: low.name, score: (low.value * 10).toFixed(1) },
    };
  }, [countriesData, costFile, slug, lang]);
}

interface DimCardProps {
  /** Slug for i18n + data-testid. Covers the 10 scoring dimensions plus
   *  one virtual `property_tax` card that lives only in marketing — the
   *  scoring engine doesn't carry a property-tax weight today (KB lookup
   *  is a separate UI surface, see PROPERTY_TAX_INTEGRATION.md). */
  slug: Dimension | 'property_tax';
}

function DimCard({ slug }: DimCardProps) {
  const { t, i18n } = useTranslation();
  const range = useDimensionRange(slug);
  // Property-tax has no scoring color of its own; we borrow tax's color
  // so the two cards read as siblings in the same family.
  const colorKey = (slug === 'property_tax' ? 'tax' : slug) as Dimension;
  const href = localizePath(
    `/topics/${TOPIC_URL_SLUG[slug]}`,
    uiLang(i18n.language),
  );
  return (
    <Link
      to={href}
      data-testid={`dim-card-${slug}`}
      className="group flex min-h-[148px] flex-col rounded-lg border border-ink-100 bg-paper-2 p-5 text-left no-underline transition-all duration-150 hover:-translate-y-0.5 hover:border-ink-300 hover:bg-paper-2/80 hover:shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-terra-600 motion-reduce:hover:translate-y-0"
    >
      <span
        aria-hidden="true"
        className={cn('mb-3 inline-block h-2 w-2 rounded-full', DIM_COLORS[colorKey])}
      />
      <h3 className="font-display text-lg font-medium italic tracking-tight text-ink-900 group-hover:text-terra-700">
        {t(`home.dims.card.${slug}.title`)}
      </h3>
      <p className="mt-2 font-mono text-[11px] tracking-wider text-ink-500">
        {t(`home.dims.card.${slug}.source`)}
      </p>
      <p className="mt-3 font-mono text-[11px] text-ink-400">
        {range
          ? t('home.dims.rangeExample', {
              high: range.high.name,
              highScore: range.high.score,
              low: range.low.name,
              lowScore: range.low.score,
            })
          : t(`home.dims.card.${slug}.range`, { defaultValue: '' })}
      </p>
    </Link>
  );
}

// Insert the virtual `property_tax` card right after the `tax` (income tax)
// card so the two read as a pair. If `tax` isn't in DIMENSIONS for some
// reason, fall back to appending property_tax at the end.
function buildCards(): Array<Dimension | 'property_tax'> {
  const out: Array<Dimension | 'property_tax'> = [];
  let inserted = false;
  for (const d of DIMENSIONS) {
    out.push(d);
    if (d === 'tax' && !inserted) {
      out.push('property_tax');
      inserted = true;
    }
  }
  if (!inserted) out.push('property_tax');
  return out;
}

export function DimensionsGrid() {
  const { t } = useTranslation();
  const cards = buildCards();
  return (
    <section
      id="dimensions"
      aria-labelledby="dims-title"
      className="bg-paper"
    >
      <div className="mx-auto max-w-5xl px-6 py-16 sm:py-20">
        <h2
          id="dims-title"
          className="font-display text-3xl font-medium tracking-tight text-ink-900 sm:text-4xl"
        >
          {t('home.dims.title')}
        </h2>
        <p className="mt-3 max-w-2xl text-base leading-relaxed text-ink-500">
          {t('home.dims.sub')}
        </p>

        <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {cards.map((slug) => (
            <DimCard key={slug} slug={slug} />
          ))}
        </div>
      </div>
    </section>
  );
}
